import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { PublicHTTPService } from '../../../services/publicHttpService';
import JobviousLogo from "./../../../assets/Jobvious_logo@2x.svg";

const API = new PublicHTTPService();

const Unsubscribe = () => {
  const { token } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  const [step, setStep] = useState('confirm'); // confirm, processing, success, already, error
  const [businessName, setBusinessName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await API.get(`/public/unsubscribe/${token}`);
      if (response.data.success) {
        setBusinessName(response.data.businessName || '');
        if (response.data.alreadyUnsubscribed) {
          setStep('already');
        }
      } else {
        setError(response.data.message || 'This unsubscribe link is invalid or has expired.');
        setStep('error');
      }
    } catch (err) {
      console.error('Error validating unsubscribe token:', err);
      setError('This unsubscribe link is invalid or has expired.');
      setStep('error');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setStep('processing');
      const response = await API.post(`/public/unsubscribe/${token}`);

      if (response.data.success) {
        setStep('success');
      } else {
        setError(response.data.message || 'Unable to process your request. Please try again.');
        setStep('error');
      }
    } catch (err) {
      console.error('Error processing unsubscribe:', err);
      setError('Unable to process your request. Please try again.');
      setStep('error');
    }
  };

  // Processing state
  if (step === 'processing') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Processing...</span>
            </div>
            <p className="mt-3">Processing your request...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="card shadow">
              <div className="card-body py-5">
                <div className="text-danger mb-4">
                  <i className="fa-sharp fa-solid fa-exclamation-circle" style={{ fontSize: '48px' }}></i>
                </div>
                <h4>Something Went Wrong</h4>
                <p className="text-muted">{error}</p>
                <p className="text-muted mt-3">
                  <small>If you continue to have trouble, please contact the business directly to manage your email preferences.</small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already unsubscribed state
  if (step === 'already') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="card shadow">
              <div className="card-body py-5">
                <div className="text-muted mb-4">
                  <i className="fa-sharp fa-solid fa-envelope-circle-check" style={{ fontSize: '48px' }}></i>
                </div>
                <h4>Already Unsubscribed</h4>
                <p className="text-muted">
                  {email ? <strong>{email}</strong> : 'Your email'} has already been removed from
                  {businessName ? <strong> {businessName}'s</strong> : ' this'} mailing list.
                </p>
                <p className="text-muted mt-3">
                  <small>You will not receive any further marketing emails from this business.</small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 text-center">
            <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
            <div className="card shadow">
              <div className="card-body py-5">
                <div className="text-success mb-4">
                  <i className="fa-sharp fa-solid fa-check-circle" style={{ fontSize: '64px' }}></i>
                </div>
                <h3>You've Been Unsubscribed</h3>
                <p className="lead text-muted mt-3">
                  {email ? <strong>{email}</strong> : 'Your email'} has been removed from
                  {businessName ? <strong> {businessName}'s</strong> : ' this'} mailing list.
                </p>
                <hr className="my-4" />
                <p className="text-muted">
                  You will no longer receive marketing emails from this business.
                  Please allow up to 24 hours for changes to take full effect.
                </p>
                <p className="text-muted mt-3">
                  <small>
                    Note: You may still receive transactional emails related to active services or appointments.
                  </small>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirm state (default)
  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6 text-center">
          <img src={JobviousLogo} alt="Jobvious" style={{ width: '200px', marginBottom: '30px' }} />
          <div className="card shadow">
            <div className="card-body py-5">
              <div className="text-muted mb-4">
                <i className="fa-sharp fa-solid fa-envelope-open" style={{ fontSize: '48px' }}></i>
              </div>
              <h4>Unsubscribe from Emails</h4>
              <p className="text-muted mt-3">
                Are you sure you want to unsubscribe
                {email && <><br /><strong>{email}</strong></>}
                {businessName && <> from <strong>{businessName}'s</strong></>} mailing list?
              </p>
              <button
                className="btn btn-danger btn-lg mt-3"
                onClick={handleUnsubscribe}
              >
                <i className="fa-sharp fa-solid fa-envelope-circle-xmark mr-2"></i>
                Unsubscribe
              </button>
              <p className="text-muted mt-4">
                <small>
                  You will no longer receive marketing emails from this business.
                  Transactional emails for active services will not be affected.
                </small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
